/**
 * GreenSyn Inventory — shared, runtime-validated schemas (zod).
 *
 * The API validates every request body with these; the web app uses the same
 * types so the two can never drift. Money and quantities are decimal STRINGS.
 */
import { z } from 'zod';
import { CAPABILITIES, ROLE_CODE_PATTERN, SECTIONS } from './auth.js';
import {
  BATCH_STATUSES,
  GST_REGISTRATION_TYPES,
  ITEM_KINDS,
  LOCATION_TYPES,
  LOT_ORIGINS,
  LOT_STATUSES,
  MOVEMENT_TYPES,
  QC_STATUSES,
  RECIPE_STATUSES,
  RECOVERY_CREDIT_POLICIES,
  RELEASE_POLICIES,
  TOLERANCE_TYPES,
} from './enums.js';
import {
  GST_STATE_CODES,
  GSTIN_PATTERN,
  gstStateCodeFromGstin,
  gstinCheckCharacter,
  normaliseGstin,
} from './gst.js';
import { GST_CAPTCHA_PATTERN } from './gstLookup.js';

/* -------------------------------------------------------------------------- */
/* Primitives                                                                  */
/* -------------------------------------------------------------------------- */

/** A decimal quantity/cost as a string, e.g. "12.5". Never a JS number. */
export const decimalString = z
  .string()
  .trim()
  .regex(/^-?\d+(\.\d+)?$/, 'must be a decimal number');

export const nonNegativeDecimal = decimalString.refine(
  (value) => !value.startsWith('-'),
  'must not be negative',
);

export const positiveDecimal = nonNegativeDecimal.refine(
  (value) => Number(value) > 0,
  'must be greater than zero',
);

export const percentage = decimalString.refine(
  (value) => !value.startsWith('-') && Number(value) <= 100,
  'must be between 0 and 100',
);

/** Unit of measure code, e.g. "kg", "L", "ea". */
export const uom = z
  .string()  .trim()
  .min(1)
  .max(16)
  .regex(/^[A-Za-z%]+$/, 'must be letters only');

export const uuid = z.string().uuid();

/** `{ id }` route params. */
export const idParam = z.object({ id: uuid });

/**
 * Query-string booleans. `z.coerce.boolean()` is wrong here: Boolean('false')
 * is true, so `?includeInactive=false` would silently mean "yes".
 */
export const booleanQuery = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((value) => value === true || value === 'true');

/** ISO-8601 timestamp accepted on input. */
export const isoDateTime = z.string().datetime({ offset: true });

export const shortText = z.string().trim().min(1).max(200);
export const longText = z.string().trim().max(4000);
export const optionalReason = z.string().trim().min(3).max(1000).optional();

/**
 * A role code. Lower-cased and restricted to what needs no escaping in a URL, a
 * log line or a CSV cell. The database carries a CHECK with the same pattern.
 */
export const roleCode = z
  .string()
  .trim()
  .toLowerCase()
  .regex(ROLE_CODE_PATTERN, 'lowercase letters, digits and underscore; must start with a letter');

/* -------------------------------------------------------------------------- */
/* Pagination                                                                  */
/* -------------------------------------------------------------------------- */

export const paginationQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type PaginationQuery = z.infer<typeof paginationQuery>;

export const paginated = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    items: z.array(item),
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
  });

/* -------------------------------------------------------------------------- */
/* Errors                                                                      */
/* -------------------------------------------------------------------------- */

export const errorResponse = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ErrorResponse = z.infer<typeof errorResponse>;

/* -------------------------------------------------------------------------- */
/* Health / readiness                                                          */
/* -------------------------------------------------------------------------- */

export const healthResponse = z.object({
  status: z.literal('ok'),
  service: z.string(),
  version: z.string(),
  uptimeSeconds: z.number(),
  timestamp: isoDateTime,
});
export type HealthResponse = z.infer<typeof healthResponse>;

export const readyResponse = z.object({
  status: z.enum(['ready', 'degraded']),
  database: z.enum(['up', 'down']),
  timestamp: isoDateTime,
});
export type ReadyResponse = z.infer<typeof readyResponse>;

/* -------------------------------------------------------------------------- */
/* Auth (login lands in M1; the contract is fixed here)                         */
/* -------------------------------------------------------------------------- */

export const loginRequest = z.object({
  email: z.string().trim().email().max(320),
  password: z.string().min(8).max(200),
});
export type LoginRequest = z.infer<typeof loginRequest>;

export const sessionUser = z.object({
  id: uuid,
  email: z.string().email(),
  name: z.string(),
  roles: z.array(
    z.object({
      id: uuid,
      code: z.string(),
      name: z.string(),
      sections: z.array(z.enum(SECTIONS)),
    }),
  ),
  defaultRoleId: uuid.nullable(),
  mustChangePassword: z.boolean(),
  passwordChangedAt: isoDateTime.nullable(),
});

/* -------------------------------------------------------------------------- */
/* Master data (M1)                                                            */
/* -------------------------------------------------------------------------- */

export const createItemRequest = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .max(40)
    .regex(/^[A-Za-z0-9._-]+$/, 'letters, digits, dot, dash and underscore only'),
  name: shortText,
  kind: z.enum(ITEM_KINDS),
  baseUom: uom,
  trackLots: z.boolean().default(true),
  shelfLifeDays: z.number().int().positive().optional(),
  reorderPoint: nonNegativeDecimal.optional(),
  recoveredHandling: z.enum(RELEASE_POLICIES).default('quarantine'),
  recoveryCreditPolicy: z.enum(RECOVERY_CREDIT_POLICIES).default('percent_of_input_cost'),
  /** Fraction of input cost credited to a recovery of this item, 0-100. */
  recoveryCreditPercent: percentage.default('100'),
});
export type CreateItemRequest = z.infer<typeof createItemRequest>;

export const createLocationRequest = z.object({
  code: z.string().trim().min(1).max(40),
  name: shortText,
  type: z.enum(LOCATION_TYPES),
  requiresQcRelease: z.boolean().default(false),
});
export type CreateLocationRequest = z.infer<typeof createLocationRequest>;

export const updateItemRequest = z
  .object({
    name: shortText.optional(),
    kind: z.enum(ITEM_KINDS).optional(),
    baseUom: uom.optional(),
    trackLots: z.boolean().optional(),
    shelfLifeDays: z.number().int().positive().nullable().optional(),
    reorderPoint: nonNegativeDecimal.nullable().optional(),
    recoveredHandling: z.enum(RELEASE_POLICIES).optional(),
    recoveryCreditPolicy: z.enum(RECOVERY_CREDIT_POLICIES).optional(),
    recoveryCreditPercent: percentage.optional(),
    isActive: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'at least one field must be provided',
  });
export type UpdateItemRequest = z.infer<typeof updateItemRequest>;

export const updateLocationRequest = z
  .object({
    name: shortText.optional(),
    type: z.enum(LOCATION_TYPES).optional(),
    requiresQcRelease: z.boolean().optional(),
    isActive: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'at least one field must be provided',
  });
export type UpdateLocationRequest = z.infer<typeof updateLocationRequest>;

/**
 * A GSTIN, validated on its **check digit** as well as its shape.
 *
 * The shape alone accepts any 15 characters that look right, which is exactly
 * the kind of mistake that reaches a tax invoice: the checksum is what catches a
 * mistyped or transposed character. The message names the character it should
 * end in, because "invalid GSTIN" gives nobody anything to act on.
 */
export const gstin = z
  .string()
  .transform(normaliseGstin)
  .superRefine((value, ctx) => {
    if (!GSTIN_PATTERN.test(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'a GSTIN is 15 characters: 2 state digits, the 10-character PAN, the entity number, a reserved character and a check digit',
      });
      return;
    }

    const expected = gstinCheckCharacter(value.slice(0, 14));
    if (expected !== value[14]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `the check digit is wrong — a GSTIN starting '${value.slice(0, 14)}' must end in '${expected}'`,
      });
      return;
    }

    // A GSTIN whose first two digits are not a state code is well-formed but
    // meaningless — the checksum cannot know that the state never existed.
    if (gstStateCodeFromGstin(value) === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `'${value.slice(0, 2)}' is not a GST state code — check the first two digits`,
      });
    }
  });

/** A GST state code: the first two digits of every GSTIN. */
export const gstStateCode = z.enum(GST_STATE_CODES);

/**
 * Absent, blank and null all mean the same thing to a form, so they are folded
 * together here rather than in every route. `null` is how a PATCH clears a field.
 */
export const optionalGstin = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  gstin.nullable().optional(),
);

export const optionalGstStateCode = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  gstStateCode.nullable().optional(),
);

/**
 * A free-text field a form may leave blank. Blank and null both mean "absent",
 * so a cleared field clears the column rather than storing an empty string that
 * then renders as neither a value nor a dash.
 */
export const optionalLongText = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  longText.nullable().optional(),
);

/** A date-only value in ISO form. Stricter than `Date.parse` alone: the day must exist. */
function isRealIsoDate(value: string): boolean {
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

/** ISO `YYYY-MM-DD`, or absent/blank for none. */
export const optionalIsoDate = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'a date is YYYY-MM-DD')
    .refine(isRealIsoDate, 'that is not a real calendar date')
    .nullable()
    .optional(),
);

/**
 * The GST portal's reference fields for a supplier, shared by create and update.
 *
 * They arrive together from a lookup, so they are treated as one group: a request
 * that sends any of them is supplying the record and replaces all of them. That
 * is what stops a stale registration date or status surviving a re-lookup, and
 * what clears them when the GSTIN changes — see the `suppliers` service.
 */
const gstPortalDetails = {
  gstLegalName: optionalLongText,
  gstTradeName: optionalLongText,
  gstStatus: optionalLongText,
  gstTaxpayerType: optionalLongText,
  gstConstitution: optionalLongText,
  gstRegisteredOn: optionalIsoDate,
  gstPrincipalPlace: optionalLongText,
  gstNatureOfBusiness: z.array(shortText).max(32).optional(),
};

export const createSupplierRequest = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .max(40)
    .regex(/^[A-Za-z0-9._-]+$/, 'letters, digits, dot, dash and underscore only'),
  name: shortText,
  contact: optionalLongText,
  gstin: optionalGstin,
  gstRegistrationType: z.enum(GST_REGISTRATION_TYPES).nullable().optional(),
  gstStateCode: optionalGstStateCode,
  ...gstPortalDetails,
  notes: optionalLongText,
});
export type CreateSupplierRequest = z.infer<typeof createSupplierRequest>;

/**
 * Field-level validation only. The rules that relate the GST fields to each
 * other depend on the stored record — sending `{gstRegistrationType: 'regular'}`
 * is legal or not depending on whether that supplier already has a GSTIN — so
 * they run against the merged record in `supplierGstIssues`.
 */
export const updateSupplierRequest = z
  .object({
    name: shortText.optional(),
    contact: optionalLongText,
    gstin: optionalGstin,
    gstRegistrationType: z.enum(GST_REGISTRATION_TYPES).nullable().optional(),
    gstStateCode: optionalGstStateCode,
    ...gstPortalDetails,
    notes: optionalLongText,
    isActive: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'at least one field must be provided',
  });
export type UpdateSupplierRequest = z.infer<typeof updateSupplierRequest>;

/** Master-data list filters. */
export const masterDataQuery = paginationQuery.extend({
  search: z.string().trim().max(120).optional(),
  includeInactive: booleanQuery.default(false),
});
export type MasterDataQuery = z.infer<typeof masterDataQuery>;

/* -------------------------------------------------------------------------- */
/* Code suggestions                                                            */
/* -------------------------------------------------------------------------- */

/**
 * What each generator needs to build a suggestion. These constrain the
 * *inputs* to the generator, never the code a person may store — the code
 * fields themselves stay free text, because a lot often has to carry the
 * supplier's own batch number.
 */
export const suggestItemCodeQuery = z.object({
  kind: z.enum(ITEM_KINDS),
  name: shortText,
});

export const suggestLocationCodeQuery = z.object({
  type: z.enum(LOCATION_TYPES),
  name: shortText,
});

/** Suppliers and recipes both derive their mnemonic from the record's name. */
export const suggestByNameQuery = z.object({
  name: shortText,
});

export const suggestLotCodeQuery = z.object({ itemId: uuid });
/** A batch code names the product and the site, so it needs both. */
export const suggestBatchCodeQuery = z.object({ itemId: uuid, locationId: uuid });

/** A server-generated code plus its decoded parts, for display. */
export const suggestedCodeResponse = z.object({
  code: z.string(),
  segments: z.array(z.object({ value: z.string(), label: z.string() })),
});
export type SuggestedCodeResponse = z.infer<typeof suggestedCodeResponse>;

/**
 * A request to read a GSTIN from the portal.
 *
 * The GSTIN goes through the *same* schema as a supplier record, so a checksum
 * failure is refused here with the "must end in …" message rather than being
 * sent to the portal and coming back as a bare SWEB_9035. The captcha is the six
 * digits off the image; the cookie is opaque and echoed back exactly as the
 * captcha call returned it — the portal ties the two together and this app does
 * not presume to interpret it.
 */
export const gstLookupRequest = z.object({
  gstin,
  captcha: z.string().trim().regex(GST_CAPTCHA_PATTERN, 'the captcha is the six digits on the image'),
  captchaCookie: z.string().trim().min(1).max(200),
});
export type GstLookupRequest = z.infer<typeof gstLookupRequest>;

/* -------------------------------------------------------------------------- */
/* Users (M1, admin only)                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Password policy for *new* passwords: length is the strongest lever, so we ask
 * for 12 rather than composing character-class rules.
 */
export const newPassword = z.string().min(12, 'must be at least 12 characters').max(200);

/**
 * A user's roles. Plural, and at least one: a user with no role can sign in and
 * do nothing, which is never what an administrator meant to do.
 */
const roleIds = z.array(uuid).min(1, 'a user needs at least one role').max(20);
const roleCodes = z.array(roleCode).min(1, 'a user needs at least one role').max(20);

export const createUserRequest = z
  .object({
    email: z.string().trim().email().max(320),
    name: shortText,
    password: newPassword,
    /** Supply one of these. Neither means the `viewer` role. */
    roleIds: roleIds.optional(),
    roleCodes: roleCodes.optional(),
    /**
     * The administrator chose this password, so it is a temporary one by default:
     * it was very likely handed over out of band, and must be replaced at the
     * first sign-in. Pass `false` to provision an account that keeps it.
     */
    mustChangePassword: z.boolean().default(true),
  })
  .refine((value) => !(value.roleIds && value.roleCodes), {
    message: 'provide either roleIds or roleCodes, not both',
    path: ['roleCodes'],
  });
export type CreateUserRequest = z.infer<typeof createUserRequest>;

export const updateUserRequest = z
  .object({
    name: shortText.optional(),
    /** Replaces the whole set when given. */
    roleIds: roleIds.optional(),
    roleCodes: roleCodes.optional(),
    isActive: z.boolean().optional(),
    password: newPassword.optional(),
    /**
     * Whether the holder must replace the password before doing anything else.
     * An administrator's reset sets it by default; it is cleared automatically
     * when the holder changes their own password.
     */
    mustChangePassword: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'at least one field must be provided',
  })
  .refine((value) => !(value.roleIds && value.roleCodes), {
    message: 'provide either roleIds or roleCodes, not both',
    path: ['roleCodes'],
  })
  .refine((value) => value.mustChangePassword !== false || value.password !== undefined, {
    // Clearing the flag while the holder is still using the administrator's
    // password would silently skip the forced change. Clearing it is only
    // meaningful alongside a new password.
    message: 'a password must be supplied to clear mustChangePassword',
    path: ['mustChangePassword'],
  });
export type UpdateUserRequest = z.infer<typeof updateUserRequest>;

/* -------------------------------------------------------------------------- */
/* Account (self-service)                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Changing your own password. The current password is required even though the
 * caller already holds a session: it is what stops a stolen cookie from being
 * quietly turned into a permanent account takeover. Self-scoped, so it needs no
 * capability — only a signed-in user.
 */
export const changePasswordRequest = z
  .object({
    currentPassword: z.string().min(1).max(200),
    newPassword,
  })
  .refine((value) => value.newPassword !== value.currentPassword, {
    message: 'the new password must be different from the current one',
    path: ['newPassword'],
  });
export type ChangePasswordRequest = z.infer<typeof changePasswordRequest>;

/**
 * Editing your own profile. Name only: an email address is the login identity and
 * changing it needs uniqueness and re-verification, which is a different feature.
 */
export const updateProfileRequest = z.object({
  name: shortText,
});
export type UpdateProfileRequest = z.infer<typeof updateProfileRequest>;

/* -------------------------------------------------------------------------- */
/* Roles (custom roles)                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Create a custom role. The capability list is a closed enum: an administrator
 * can grant any *existing* capability but cannot invent one. The database has a
 * matching CHECK constraint, so this is enforced even against a hand-written
 * INSERT.
 *
 * `sections` is the role's workspace — which screens its view presents. It is
 * deliberately separate from `capabilities` so a role can be shown a screen it
 * cannot act on (a reviewer who watches production but must not run it). Omitted
 * means "suggest them from the capabilities", which is almost always right.
 */
export const createRoleRequest = z.object({
  code: roleCode,
  name: shortText,
  description: longText.optional(),
  /** An empty list is valid, and means "can read, cannot change" — like viewer. */
  capabilities: z.array(z.enum(CAPABILITIES)).default([]),
  sections: z.array(z.enum(SECTIONS)).optional(),
});
export type CreateRoleRequest = z.infer<typeof createRoleRequest>;

/**
 * Editing a role.
 *
 * `code` is absent on purpose: a role's code is what audit entries and URLs refer
 * to, so renaming it would rewrite history's meaning. `sections` is editable even
 * on a built-in role — a workspace is a preference, not a permission, and a site
 * that wants its warehouse screens ordered differently should be able to say so
 * without inventing a parallel role.
 */
export const updateRoleRequest = z
  .object({
    name: shortText.optional(),
    description: longText.nullable().optional(),
    capabilities: z.array(z.enum(CAPABILITIES)).optional(),
    sections: z.array(z.enum(SECTIONS)).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'at least one field must be provided',
  });
export type UpdateRoleRequest = z.infer<typeof updateRoleRequest>;

export const roleQuery = paginationQuery.extend({
  search: z.string().trim().max(120).optional(),
});
export type RoleQuery = z.infer<typeof roleQuery>;

/* -------------------------------------------------------------------------- */
/* Inventory ledger (M2)                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Receive stock. Creates the lot if it does not exist — the lot code is
 * auto-suggested (see GET /inventory/lots/suggest-code) but the user may
 * override it with a supplier's own batch number.
 */
export const receiveStockRequest = z.object({
  itemId: uuid,
  locationId: uuid,
  /** Omit to accept the suggested code. */
  lotCode: z.string().trim().min(1).max(64).optional(),
  quantity: positiveDecimal,
  uom,
  /** Purchase cost per entered UoM. Stored on the lot, converted to base UoM. */
  unitCost: nonNegativeDecimal,
  supplierId: uuid.optional(),
  expiryDate: isoDateTime.optional(),
  occurredAt: isoDateTime.optional(),
  reason: longText.optional(),
});
export type ReceiveStockRequest = z.infer<typeof receiveStockRequest>;

/** Draw stock out against a reason (not tied to a production batch — that is M4). */
export const issueStockRequest = z.object({
  itemId: uuid,
  lotId: uuid,
  locationId: uuid,
  quantity: positiveDecimal,
  uom,
  reason: longText.optional(),
  occurredAt: isoDateTime.optional(),
});
export type IssueStockRequest = z.infer<typeof issueStockRequest>;

/** Move stock between locations. One logical move, two ledger rows. */
export const transferStockRequest = z
  .object({
    lotId: uuid,
    fromLocationId: uuid,
    toLocationId: uuid,
    quantity: positiveDecimal,
    uom,
    reason: longText.optional(),
    occurredAt: isoDateTime.optional(),
  })
  .refine((value) => value.fromLocationId !== value.toLocationId, {
    message: 'source and destination must differ',
    path: ['toLocationId'],
  });
export type TransferStockRequest = z.infer<typeof transferStockRequest>;

/** Scrap is a deliberate loss, so it is never silent. */
export const scrapStockRequest = z.object({
  lotId: uuid,
  locationId: uuid,
  quantity: positiveDecimal,
  uom,
  reason: z.string().trim().min(3).max(1000),
  occurredAt: isoDateTime.optional(),
});
export type ScrapStockRequest = z.infer<typeof scrapStockRequest>;

/**
 * A stock-count correction. `quantityDelta` is signed: positive when the count
 * found more than the system believed, negative when it found less.
 */
export const adjustStockRequest = z.object({
  lotId: uuid,
  locationId: uuid,
  quantityDelta: decimalString.refine((value) => Number(value) !== 0, 'must not be zero'),
  uom,
  /** Adjustments always require an explanation — see docs/PLAN.md section 7. */
  reason: z.string().trim().min(3).max(1000),
  occurredAt: isoDateTime.optional(),
});
export type AdjustStockRequest = z.infer<typeof adjustStockRequest>;

/**
 * Reverse a movement. The ledger is append-only, so a correction is a new row
 * with the opposite sign that points at the row it undoes — never an edit.
 */
export const reverseMovementRequest = z.object({
  reason: z.string().trim().min(3).max(1000),
});
export type ReverseMovementRequest = z.infer<typeof reverseMovementRequest>;

export const movementQuery = paginationQuery.extend({
  itemId: uuid.optional(),
  lotId: uuid.optional(),
  locationId: uuid.optional(),
  type: z.enum(MOVEMENT_TYPES).optional(),
  from: isoDateTime.optional(),
  to: isoDateTime.optional(),
});
export type MovementQuery = z.infer<typeof movementQuery>;

export const balanceQuery = paginationQuery.extend({
  itemId: uuid.optional(),
  lotId: uuid.optional(),
  locationId: uuid.optional(),
  search: z.string().trim().max(120).optional(),
  /** On-hand of zero is not interesting by default. */
  hideEmpty: booleanQuery.default(true),
});
export type BalanceQuery = z.infer<typeof balanceQuery>;

export const lotQuery = paginationQuery.extend({
  itemId: uuid.optional(),
  locationId: uuid.optional(),
  status: z.enum(LOT_STATUSES).optional(),
  origin: z.enum(LOT_ORIGINS).optional(),
  search: z.string().trim().max(120).optional(),
  includeEmpty: booleanQuery.default(false),
  expiringBefore: isoDateTime.optional(),
});
export type LotQuery = z.infer<typeof lotQuery>;

/* -------------------------------------------------------------------------- */
/* Recipes / BOM (M3)                                                          */
/* -------------------------------------------------------------------------- */

/**
 * One input line. `toleranceType` and `toleranceValue` must be given together —
 * a tolerance that says neither how much nor in what terms is worse than none.
 */
export const recipeComponentInput = z
  .object({
    itemId: uuid,
    quantity: positiveDecimal,
    uom,
    toleranceType: z.enum(TOLERANCE_TYPES).nullable().default(null),
    toleranceValue: nonNegativeDecimal.nullable().default(null),
    /** Optional components may be skipped without an override. */
    isOptional: z.boolean().default(false),
    note: longText.optional(),
    /** Items that may be drawn instead of this one. */
    substituteItemIds: z.array(uuid).default([]),
  })
  .refine((value) => (value.toleranceType === null) === (value.toleranceValue === null), {
    message: 'tolerance type and value must be provided together',
    path: ['toleranceValue'],
  });
export type RecipeComponentInput = z.infer<typeof recipeComponentInput>;

export const createRecipeRequest = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .max(40)
    .regex(/^[A-Za-z0-9._-]+$/, 'letters, digits, dot, dash and underscore only'),
  name: shortText,
  /** The item this recipe produces. */
  itemId: uuid,
  /** How much it produces — the basis the component quantities are stated for. */
  outputQuantity: positiveDecimal,
  outputUom: uom,
  notes: longText.optional(),
  components: z.array(recipeComponentInput).min(1, 'a recipe needs at least one component'),
});
export type CreateRecipeRequest = z.infer<typeof createRecipeRequest>;

export const updateRecipeRequest = z
  .object({
    name: shortText.optional(),
    outputQuantity: positiveDecimal.optional(),
    outputUom: uom.optional(),
    notes: longText.nullable().optional(),
    /** When provided, replaces the whole component list. */
    components: z.array(recipeComponentInput).min(1).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'at least one field must be provided',
  });
export type UpdateRecipeRequest = z.infer<typeof updateRecipeRequest>;

export const recipeQuery = paginationQuery.extend({
  itemId: uuid.optional(),
  status: z.enum(RECIPE_STATUSES).optional(),
  search: z.string().trim().max(120).optional(),
  /** Only the newest version of each recipe code. */
  latestOnly: booleanQuery.default(true),
});
export type RecipeQuery = z.infer<typeof recipeQuery>;

export const approveRecipeRequest = z.object({
  /** Optional note recorded against the approval. */
  note: longText.optional(),
});
export type ApproveRecipeRequest = z.infer<typeof approveRecipeRequest>;

/* -------------------------------------------------------------------------- */
/* Recovery and quality control (M5)                                           */
/* -------------------------------------------------------------------------- */

/**
 * Record material recovered from a batch (or standalone from named lots) into
 * inventory, carrying the `recovered` origin tag.
 */
export const createRecoveryRequest = z.object({
  /** The material being recovered, e.g. reclaimed solvent. */
  itemId: uuid,
  /** The batch it came from. Omit for a standalone recovery. */
  batchId: uuid.optional(),
  /** Where the recovered material is put. */
  locationId: uuid,
  quantity: positiveDecimal,
  uom,
  /** Which step of the process it came out of. */
  recoveryStep: z.string().trim().min(1).max(200),
  method: longText.optional(),
  grade: z.string().trim().max(80).optional(),
  purityPct: percentage.optional(),
  /** The lots the material actually came from. Always required. */
  sourceLotIds: z.array(uuid).min(1, 'at least one contributing lot is required'),
  /** Omit to accept the suggested code. */
  lotCode: z.string().trim().min(1).max(64).optional(),
  expiryDate: isoDateTime.optional(),
  /**
   * Overrides the item's default release policy for this one recovery. Requires
   * `recovery.overridePolicy` (or being the recorder) and a reason.
   */
  releasePolicy: z.enum(RELEASE_POLICIES).optional(),
  policyOverrideReason: optionalReason,
  /** Omit to let the item's credit policy price it. */
  creditedUnitCost: nonNegativeDecimal.optional(),
  occurredAt: isoDateTime.optional(),
  notes: longText.optional(),
});
export type CreateRecoveryRequest = z.infer<typeof createRecoveryRequest>;

export const recoveryQuery = paginationQuery.extend({
  itemId: uuid.optional(),
  batchId: uuid.optional(),
  search: z.string().trim().max(120).optional(),
});
export type RecoveryQuery = z.infer<typeof recoveryQuery>;

/**
 * A quality decision on a lot. `passed` releases quarantined material into
 * usable stock; `failed` marks it rejected, which is a one-way door.
 */
export const qualityCheckRequest = z.object({
  lotId: uuid,
  outcome: z.enum(['passed', 'failed']),
  result: longText.optional(),
  notes: longText.optional(),
});
export type QualityCheckRequest = z.infer<typeof qualityCheckRequest>;

export const qualityCheckQuery = paginationQuery.extend({
  lotId: uuid.optional(),
  status: z.enum(QC_STATUSES).optional(),
});
export type QualityCheckQuery = z.infer<typeof qualityCheckQuery>;

/* -------------------------------------------------------------------------- */
/* Traceability, reports and the audit trail (M6)                              */
/* -------------------------------------------------------------------------- */

export const traceQuery = z.object({
  /** Which way to walk from the starting point. Default: both. */
  direction: z.enum(['both', 'up', 'down']).default('both'),
  /** How many hops to follow. */
  depth: z.coerce.number().int().min(1).max(12).default(6),
});
export type TraceQuery = z.infer<typeof traceQuery>;

export const reportRangeQuery = z.object({
  from: isoDateTime.optional(),
  to: isoDateTime.optional(),
  itemId: uuid.optional(),
  locationId: uuid.optional(),
  /** `csv` returns a downloadable file instead of JSON. */
  format: z.enum(['json', 'csv']).default('json'),
});
export type ReportRangeQuery = z.infer<typeof reportRangeQuery>;

export const auditQuery = paginationQuery.extend({
  actorUserId: uuid.optional(),
  entityType: z.string().trim().max(60).optional(),
  entityId: z.string().trim().max(64).optional(),
  action: z.string().trim().max(80).optional(),
  from: isoDateTime.optional(),
  to: isoDateTime.optional(),
});
export type AuditQuery = z.infer<typeof auditQuery>;

/* -------------------------------------------------------------------------- */
/* Production batches (M4)                                                     */
/* -------------------------------------------------------------------------- */

export const createBatchRequest = z.object({
  /**
   * Omit to accept the suggested code. 40 characters, like every other code;
   * the generator shortens the product segment of a long item code to fit
   * (`BATCH_CODE_MAX_LENGTH` in @gsls/shared/codes).
   */
  code: z.string().trim().min(1).max(40).optional(),
  /** The recipe version this batch is pinned to. Must be approved. */
  recipeId: uuid,
  /** How much this batch is meant to produce, in the recipe's output unit. */
  targetQuantity: positiveDecimal,
  /** Where the output is produced and the output lot will sit. */
  locationId: uuid,
  ownerUserId: uuid.optional(),
  scheduledFor: isoDateTime.optional(),
  notes: longText.optional(),
});
export type CreateBatchRequest = z.infer<typeof createBatchRequest>;

/**
 * Record a draw against the batch. The movement posts immediately, so on-hand
 * always reflects what has really been used.
 */
export const recordConsumptionRequest = z.object({
  /** Omit for an ad-hoc draw that no recipe component called for. */
  componentId: uuid.optional(),
  itemId: uuid,
  lotId: uuid,
  locationId: uuid,
  quantity: positiveDecimal,
  uom,
  /** Draws a substitute in place of the component's own item. */
  isSubstitute: z.boolean().default(false),
  /**
   * Required for ad-hoc lines, substitutes, and any draw that takes the total
   * outside the component's tolerance.
   */
  reason: longText.optional(),
});
export type RecordConsumptionRequest = z.infer<typeof recordConsumptionRequest>;

export const completeBatchRequest = z.object({
  /** Actual output. Defaults to the batch target when omitted. */
  outputQuantity: positiveDecimal.optional(),
  /** Defaults to the batch's own location. */
  outputLocationId: uuid.optional(),
  outputLotCode: z.string().trim().min(1).max(64).optional(),
  expiryDate: isoDateTime.optional(),
  notes: longText.optional(),
});
export type CompleteBatchRequest = z.infer<typeof completeBatchRequest>;

export const closeBatchRequest = z.object({
  note: longText.optional(),
});
export type CloseBatchRequest = z.infer<typeof closeBatchRequest>;

export const cancelBatchRequest = z.object({
  reason: z.string().trim().min(3).max(1000),
});
export type CancelBatchRequest = z.infer<typeof cancelBatchRequest>;

export const batchQuery = paginationQuery.extend({
  status: z.enum(BATCH_STATUSES).optional(),
  itemId: uuid.optional(),
  search: z.string().trim().max(120).optional(),
});
export type BatchQuery = z.infer<typeof batchQuery>;
